import { useEffect, useRef, useState } from "react";
import { MessageCircle } from "lucide-react";
import { useWhatsApp } from "@/hooks/use-whatsapp";

const SIZE = 60;
const MARGIN = 20;
const STORAGE_KEY = "wa_float_pos";
const DRAG_THRESHOLD = 5; // px before we consider it a drag (not a click)

const WhatsAppFloat = () => {
  const { enabled, open: openWhatsApp } = useWhatsApp();
  const btnRef = useRef<HTMLButtonElement>(null);
  const [pos, setPos] = useState<{ x: number; y: number } | null>(null);
  const dragState = useRef<{
    startX: number;
    startY: number;
    origX: number;
    origY: number;
    moved: boolean;
    pointerId: number;
  } | null>(null);




  // Init position (bottom-right) + restore saved
  useEffect(() => {
    const clamp = (x: number, y: number) => ({
      x: Math.min(Math.max(MARGIN, x), window.innerWidth - SIZE - MARGIN),
      y: Math.min(Math.max(MARGIN, y), window.innerHeight - SIZE - MARGIN),
    });
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const p = JSON.parse(saved);
        setPos(clamp(p.x, p.y));
        return;
      }
    } catch {
      // Ignore parse errors from stale storage
    }
    setPos(clamp(window.innerWidth - SIZE - MARGIN, window.innerHeight - SIZE - MARGIN));

    const onResize = () => {
      setPos((prev) => (prev ? clamp(prev.x, prev.y) : prev));
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  const onPointerDown = (e: React.PointerEvent<HTMLButtonElement>) => {
    if (!pos) return;
    dragState.current = {
      startX: e.clientX,
      startY: e.clientY,
      origX: pos.x,
      origY: pos.y,
      moved: false,
      pointerId: e.pointerId,
    };
    btnRef.current?.setPointerCapture(e.pointerId);
  };

  const onPointerMove = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = dragState.current;
    if (!s) return;
    const dx = e.clientX - s.startX;
    const dy = e.clientY - s.startY;
    if (!s.moved && Math.hypot(dx, dy) < DRAG_THRESHOLD) return;
    s.moved = true;
    const nx = Math.min(Math.max(MARGIN, s.origX + dx), window.innerWidth - SIZE - MARGIN);
    const ny = Math.min(Math.max(MARGIN, s.origY + dy), window.innerHeight - SIZE - MARGIN);
    setPos({ x: nx, y: ny });
  };

  const onPointerUp = (e: React.PointerEvent<HTMLButtonElement>) => {
    const s = dragState.current;
    btnRef.current?.releasePointerCapture(e.pointerId);
    dragState.current = null;
    if (s?.moved) {
      try {
        if (pos) localStorage.setItem(STORAGE_KEY, JSON.stringify(pos));
      } catch {
        // Ignore quota errors
      }
      return; // suppress click after drag
    }
    // treat as click
    openWhatsApp();
  };

  if (!pos || !enabled) return null;

  return (
    <button
      ref={btnRef}
      aria-label="WhatsApp"
      onPointerDown={onPointerDown}
      onPointerMove={onPointerMove}
      onPointerUp={onPointerUp}
      style={{
        position: "fixed",
        left: pos.x,
        top: pos.y,
        width: SIZE,
        height: SIZE,
        touchAction: "none",
        zIndex: 60,
      }}
      className="rounded-full bg-[#25D366] text-white shadow-elevated flex items-center justify-center hover:scale-105 active:scale-95 transition-transform cursor-grab active:cursor-grabbing"
    >
      <MessageCircle size={28} className="fill-white" strokeWidth={0} />
      <span className="sr-only">Falar no WhatsApp</span>
    </button>
  );
};

export default WhatsAppFloat;
